'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  parseMarkdownSections,
  stripSectionNumber,
  parseKeyValueList,
  type MarkdownSection,
} from '../../design/templates/markdown-sections';
import { SectionCard } from '../../definition/templates/section-card';
import { SectionGrid } from '../../definition/templates/section-grid';

type DeliveryView = 'architecture' | 'storymap' | 'mvp' | 'stories';

interface DeliveryTemplateProps {
  markdown: string;
  activeView: DeliveryView;
  className?: string;
}

const SECTION_PALETTES: Record<DeliveryView, { dot: string; title: string }[]> = {
  architecture: [
    { dot: 'bg-slate-600', title: 'text-slate-900' },
    { dot: 'bg-blue-500', title: 'text-blue-900' },
    { dot: 'bg-emerald-500', title: 'text-emerald-900' },
    { dot: 'bg-violet-500', title: 'text-violet-900' },
    { dot: 'bg-rose-500', title: 'text-rose-900' },
    { dot: 'bg-amber-500', title: 'text-amber-900' },
    { dot: 'bg-cyan-500', title: 'text-cyan-900' },
    { dot: 'bg-indigo-500', title: 'text-indigo-900' },
  ],
  storymap: [
    { dot: 'bg-indigo-500', title: 'text-indigo-900' },
    { dot: 'bg-blue-500', title: 'text-blue-900' },
    { dot: 'bg-emerald-500', title: 'text-emerald-900' },
    { dot: 'bg-amber-500', title: 'text-amber-900' },
    { dot: 'bg-violet-500', title: 'text-violet-900' },
    { dot: 'bg-rose-500', title: 'text-rose-900' },
  ],
  mvp: [
    { dot: 'bg-indigo-500', title: 'text-indigo-900' },
    { dot: 'bg-emerald-500', title: 'text-emerald-900' },
    { dot: 'bg-blue-500', title: 'text-blue-900' },
    { dot: 'bg-gray-500', title: 'text-gray-900' },
    { dot: 'bg-violet-500', title: 'text-violet-900' },
    { dot: 'bg-amber-500', title: 'text-amber-900' },
    { dot: 'bg-rose-500', title: 'text-rose-900' },
  ],
  stories: [
    { dot: 'bg-indigo-500', title: 'text-indigo-900' },
    { dot: 'bg-emerald-500', title: 'text-emerald-900' },
    { dot: 'bg-blue-500', title: 'text-blue-900' },
    { dot: 'bg-amber-500', title: 'text-amber-900' },
    { dot: 'bg-violet-500', title: 'text-violet-900' },
    { dot: 'bg-rose-500', title: 'text-rose-900' },
  ],
};

const PAIRED_SECTIONS: Record<DeliveryView, [string, string][]> = {
  architecture: [
    ['security architecture', 'observability'],
  ],
  storymap: [
    ['dependencies', 'success metrics'],
  ],
  mvp: [
    ['constraints', 'risks'],
  ],
  stories: [
    ['technical stories', 'definition of done'],
  ],
};

const SERVICE_SECTIONS: Record<DeliveryView, string[]> = {
  architecture: ['core services'],
  storymap: [],
  mvp: ['must-have features', 'should-have features'],
  stories: ['p0', 'p1', 'must-have stories', 'should-have stories'],
};

function getPalette(view: DeliveryView, index: number) {
  const pal = SECTION_PALETTES[view];
  return pal[index % pal.length];
}

function shouldPair(view: DeliveryView, a: string, b: string): boolean {
  const la = stripSectionNumber(a).toLowerCase();
  const lb = stripSectionNumber(b).toLowerCase();
  return PAIRED_SECTIONS[view].some(
    ([x, y]) => (la.includes(x) && lb.includes(y)) || (la.includes(y) && lb.includes(x))
  );
}

function isServiceSection(view: DeliveryView, title: string): boolean {
  const t = stripSectionNumber(title).toLowerCase();
  return SERVICE_SECTIONS[view].some(s => t.includes(s));
}

function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g);
  return (
    <span>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-semibold text-indigo-700">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={i} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-sm font-mono border border-indigo-100">
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
          return <em key={i} className="italic text-gray-600">{part.slice(1, -1)}</em>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function RichParagraph({ text }: { text: string }) {
  return (
    <p className="text-[15px] text-gray-700 leading-relaxed mb-3">
      <RichText text={text} />
    </p>
  );
}

function parseTableRows(lines: string[]): { headers: string[]; rows: string[][] } | null {
  if (lines.length < 2) return null;
  const headerCells = lines[0].split('|').map(c => c.trim()).filter(Boolean);
  if (headerCells.length === 0) return null;
  const rows = lines.slice(2).map(row =>
    row.split('|').map(c => c.trim()).filter(Boolean)
  );
  return { headers: headerCells, rows };
}

function MarkdownTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4 rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-4 py-2.5 text-left font-semibold text-gray-700">
                <RichText text={h} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 border-t border-gray-100">
                  <RichText text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Blockquote({ text }: { text: string }) {
  return (
    <blockquote className="border-l-4 border-amber-400 bg-amber-50 px-4 py-3 my-3 rounded-r-lg text-amber-900 italic text-sm">
      <RichText text={text} />
    </blockquote>
  );
}

type BodyBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'bullet'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'blockquote'; text: string }
  | { type: 'ordered'; items: string[] };

function parseBodyBlocks(body: string): BodyBlock[] {
  if (!body) return [];
  const lines = body.split('\n');
  const blocks: BodyBlock[] = [];
  let currentParagraph: string[] = [];
  let currentBullets: string[] = [];
  let currentOrdered: string[] = [];
  let currentTable: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      blocks.push({ type: 'paragraph', text: currentParagraph.join(' ').trim() });
      currentParagraph = [];
    }
  };
  const flushBullets = () => {
    if (currentBullets.length > 0) {
      blocks.push({ type: 'bullet', items: [...currentBullets] });
      currentBullets = [];
    }
  };
  const flushOrdered = () => {
    if (currentOrdered.length > 0) {
      blocks.push({ type: 'ordered', items: [...currentOrdered] });
      currentOrdered = [];
    }
  };
  const flushTable = () => {
    if (currentTable.length >= 2) {
      const parsed = parseTableRows(currentTable);
      if (parsed) blocks.push({ type: 'table', ...parsed });
    }
    currentTable = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('|')) {
      flushParagraph(); flushBullets(); flushOrdered();
      if (trimmed.match(/^\|[\s-:|]+\|$/)) {
        currentTable.push(trimmed);
      } else {
        currentTable.push(trimmed);
      }
      continue;
    }
    if (currentTable.length > 0) flushTable();

    if (trimmed.match(/^[-*]\s+/)) {
      flushParagraph(); flushOrdered();
      currentBullets.push(trimmed.replace(/^[-*]\s+/, ''));
      continue;
    }
    if (currentBullets.length > 0) flushBullets();

    if (trimmed.match(/^\d+\.\s+/)) {
      flushParagraph(); flushBullets();
      currentOrdered.push(trimmed.replace(/^\d+\.\s+/, ''));
      continue;
    }
    if (currentOrdered.length > 0) flushOrdered();

    if (trimmed.startsWith('>')) {
      flushParagraph();
      blocks.push({ type: 'blockquote', text: trimmed.replace(/^>\s*/, '') });
      continue;
    }

    if (trimmed === '' || trimmed === '---') {
      flushParagraph();
      continue;
    }

    currentParagraph.push(trimmed);
  }

  flushParagraph(); flushBullets(); flushOrdered(); flushTable();
  return blocks;
}

function BodyContent({ body }: { body: string }) {
  if (!body) return null;
  const blocks = parseBodyBlocks(body);
  if (blocks.length === 0) return null;
  return (
    <div>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return <RichParagraph key={i} text={block.text} />;
          case 'bullet':
            return (
              <ul key={i} className="space-y-1.5 mb-3">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0" />
                    <span><RichText text={item} /></span>
                  </li>
                ))}
              </ul>
            );
          case 'ordered':
            return (
              <ol key={i} className="list-decimal ml-5 space-y-1.5 mb-3 text-sm text-gray-700">
                {block.items.map((item, j) => (
                  <li key={j} className="pl-1"><RichText text={item} /></li>
                ))}
              </ol>
            );
          case 'table':
            return <MarkdownTable key={i} headers={block.headers} rows={block.rows} />;
          case 'blockquote':
            return <Blockquote key={i} text={block.text} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

function SubSectionCard({ section, accentColor }: { section: MarkdownSection; accentColor: string }) {
  const kvItems = parseKeyValueList(section.body);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
      <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', accentColor)} />
        {section.title}
      </h4>
      {kvItems.length > 0 ? (
        <dl className="space-y-2.5">
          {kvItems.map((item, i) => (
            <div key={i}>
              <dt className="text-sm font-semibold text-gray-800">{item.key}</dt>
              <dd className="text-sm text-gray-600 leading-relaxed mt-0.5">
                <RichText text={item.value} />
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <BodyContent body={section.body} />
      )}
      {section.children.length > 0 && (
        <div className="space-y-3 mt-3">
          {section.children.map((child, i) => (
            <div key={i}>
              <h5 className="text-sm font-semibold text-gray-800 mb-1">{child.title}</h5>
              <BodyContent body={child.body} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceGridSection({
  section,
  palette,
}: {
  section: MarkdownSection;
  palette: { dot: string; title: string };
}) {
  return (
    <SectionCard
      title={stripSectionNumber(section.title)}
      dotColor={palette.dot}
      titleColor={palette.title}
    >
      {section.body && <BodyContent body={section.body} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {section.children.map((child, i) => (
          <SubSectionCard key={i} section={child} accentColor={palette.dot} />
        ))}
      </div>
    </SectionCard>
  );
}

function GenericSection({
  section,
  palette,
}: {
  section: MarkdownSection;
  palette: { dot: string; title: string };
}) {
  return (
    <SectionCard
      title={stripSectionNumber(section.title)}
      dotColor={palette.dot}
      titleColor={palette.title}
    >
      <BodyContent body={section.body} />
      {section.children.length > 0 && (
        <div className="space-y-4 mt-3">
          {section.children.map((child, i) => (
            <div key={i}>
              <h4 className="text-sm font-semibold text-gray-800 mb-1.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" />
                {child.title}
              </h4>
              <div className="ml-4">
                <BodyContent body={child.body} />
                {child.children.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {child.children.map((gc, j) => (
                      <div key={j}>
                        <h5 className="text-xs font-semibold text-gray-700 mb-1">{gc.title}</h5>
                        <BodyContent body={gc.body} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

export function DeliveryTemplate({ markdown, activeView, className }: DeliveryTemplateProps) {
  const parsed = useMemo(() => parseMarkdownSections(markdown), [markdown]);

  if (parsed.sections.length === 0) {
    return (
      <div className={cn('prose prose-base max-w-none text-gray-700', className)}>
        <p>{markdown}</p>
      </div>
    );
  }

  const rendered: React.ReactNode[] = [];
  const sections = parsed.sections;
  let i = 0;
  let colorIdx = 0;

  while (i < sections.length) {
    const section = sections[i];
    const palette = getPalette(activeView, colorIdx);

    if (isServiceSection(activeView, section.title) && section.children.length > 0) {
      rendered.push(
        <ServiceGridSection key={i} section={section} palette={palette} />
      );
      i++;
      colorIdx++;
      continue;
    }

    if (
      i + 1 < sections.length &&
      shouldPair(activeView, section.title, sections[i + 1].title)
    ) {
      const nextPalette = getPalette(activeView, colorIdx + 1);
      rendered.push(
        <SectionGrid key={i}>
          <GenericSection section={section} palette={palette} />
          <GenericSection section={sections[i + 1]} palette={nextPalette} />
        </SectionGrid>
      );
      i += 2;
      colorIdx += 2;
      continue;
    }

    rendered.push(
      <GenericSection key={i} section={section} palette={palette} />
    );
    i++;
    colorIdx++;
  }

  return (
    <div className={cn('space-y-6', className)}>
      {rendered}
    </div>
  );
}
